// Copyright 2020 Sorama B.V.

import { Disposable } from "aurelia-framework";
import { IComposableCanvasControls } from "./ComposableCanvas";

export abstract class IComposableCanvasController {
    public abstract registerCanvasController(id: string, receiver: unknown, property: string): Disposable;
    public abstract registerCanvas(id: string, controls: IComposableCanvasControls): Disposable;
}

export class ComposableCanvasController implements IComposableCanvasController {
    private readonly canvases: { [id: string]: IComposableCanvasControls } = {};
    private readonly canvasControllers: { [id: string]: ((control: IComposableCanvasControls) => void)[] } = {};

    public registerCanvasController(id: string, receiver: unknown, property: string): Disposable {
        const action = (controls) => {
            receiver[property] = controls;
        };

        const controllersList = this.canvasControllers[id];
        if (controllersList) {
            controllersList.push(action);
        } else {
            this.canvasControllers[id] = [action];
        }

        this.notifyObservers(id, this.canvases[id]);

        const _this = this;
        return {
            dispose: () => {
                const list = _this.canvasControllers[id];
                if (list) {
                    const index = list.findIndex((x) => x === action);
                    list.splice(index, 1);
                }
            },
        };
    }

    public registerCanvas(id: string, controls: IComposableCanvasControls): Disposable {
        if (this.canvases[id]) {
            throw new Error("This canvas is already being controlled");
        }

        this.canvases[id] = controls;
        this.notifyObservers(id, controls);

        const _this = this;
        return {
            dispose: () => {
                const controller = _this.canvases[id];
                if (controller) {
                    delete _this.canvases[id];

                    _this.notifyObservers(id, undefined);
                }
            },
        };
    }

    private notifyObservers(id: string, controls: IComposableCanvasControls): void {
        const observers = this.canvasControllers[id];
        if (observers) {
            for (const action of observers) {
                action(controls);
            }
        }
    }
}
